<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import CsatQuestion from 'shared/components/CsatQuestion.vue';
import {
  CSAT_QUESTION_TYPES,
  isCsatRatingType,
  normalizeRatingValue,
  buildLegacyQuestions,
} from 'shared/constants/csatQuestionTypes';
import { DEFAULT_ACCENT } from './ticketStatusStyles';

const props = defineProps({
  message: { type: Object, required: true },
  accent: { type: String, default: DEFAULT_ACCENT },
  labels: { type: Object, default: () => ({}) },
  messagesUrl: { type: String, required: true },
});

const emit = defineEmits(['submitted']);

const answers = reactive({});
const submitting = ref(false);
const submitFailed = ref(false);

const attributes = computed(() => props.message.content_attributes || {});

const enabledQuestions = computed(() => {
  const provided = Array.isArray(attributes.value.questions)
    ? attributes.value.questions
    : [];
  const list = provided.length
    ? provided
    : buildLegacyQuestions(attributes.value.display_type, {
        commentPlaceholder: props.labels.feedback_placeholder,
      });
  return list.filter(question => question.enabled !== false);
});

const submittedAnswers = computed(
  () =>
    attributes.value.submitted_values?.answers || attributes.value.answers || []
);

const legacyResponse = computed(
  () =>
    attributes.value.submitted_values?.csat_survey_response ||
    attributes.value.csat_survey_response
);

const isSubmitted = computed(
  () =>
    submittedAnswers.value.length > 0 || Boolean(legacyResponse.value?.rating)
);

const isMultiChoice = question =>
  question.type === CSAT_QUESTION_TYPES.CHOICE && question.multiple;

const hasAnswerFor = question => {
  const value = answers[question.id];
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const canSubmit = computed(() =>
  enabledQuestions.value.every(
    question => !question.required || hasAnswerFor(question)
  )
);

const ratingQuestion = computed(() =>
  enabledQuestions.value.find(question => isCsatRatingType(question.type))
);

const commentQuestion = computed(() =>
  enabledQuestions.value.find(
    question => question.type === CSAT_QUESTION_TYPES.COMMENT
  )
);

const initializeAnswers = () => {
  enabledQuestions.value.forEach(question => {
    answers[question.id] = isMultiChoice(question) ? [] : null;
  });
};

const restoreSubmittedAnswers = () => {
  if (submittedAnswers.value.length) {
    const byId = new Map(
      enabledQuestions.value.map(question => [String(question.id), question])
    );
    submittedAnswers.value.forEach(answer => {
      const question = byId.get(String(answer?.question_id));
      if (!question) return;
      if (isMultiChoice(question)) {
        answers[question.id] = Array.isArray(answer.values)
          ? answer.values
          : [];
      } else if (answer.value !== undefined) {
        answers[question.id] = answer.value;
      }
    });
    return;
  }
  if (legacyResponse.value?.rating) {
    if (ratingQuestion.value) {
      answers[ratingQuestion.value.id] = legacyResponse.value.rating;
    }
    if (commentQuestion.value) {
      answers[commentQuestion.value.id] =
        legacyResponse.value.feedback_message || null;
    }
  }
};

const buildAnswersPayload = () =>
  enabledQuestions.value.filter(hasAnswerFor).map(question => {
    if (isMultiChoice(question)) {
      return {
        question_id: String(question.id),
        type: question.type,
        values: Array.isArray(answers[question.id]) ? answers[question.id] : [],
      };
    }
    return {
      question_id: String(question.id),
      type: question.type,
      value: answers[question.id],
    };
  });

const onSubmit = async () => {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  submitFailed.value = false;
  const submittedValues = {
    csat_survey_response: {
      rating: ratingQuestion.value
        ? normalizeRatingValue(
            ratingQuestion.value.type,
            answers[ratingQuestion.value.id],
            ratingQuestion.value
          )
        : null,
      feedback_message: commentQuestion.value
        ? answers[commentQuestion.value.id] || ''
        : '',
    },
    answers: buildAnswersPayload(),
  };
  try {
    const response = await fetch(`${props.messagesUrl}/${props.message.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ message: { submitted_values: submittedValues } }),
    });
    if (!response.ok) {
      submitFailed.value = true;
      return;
    }
    emit('submitted', submittedValues);
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  initializeAnswers();
  restoreSubmittedAnswers();
});
</script>

<template>
  <div
    class="whitespace-pre-line px-[15px] py-3 text-sm leading-relaxed text-[#2b2f36] rounded-[6px_18px_18px_18px] bg-[#f3f4f6] dark:bg-slate-800 dark:text-slate-100"
  >
    <div
      class="text-[13px] font-extrabold tracking-wide text-[#15181d] dark:text-white"
    >
      {{ isSubmitted ? labels.thanks : labels.title }}
    </div>
    <CsatQuestion
      v-for="question in enabledQuestions"
      :key="question.id"
      :question="question"
      :disabled="isSubmitted"
      :model-value="answers[question.id]"
      @update:model-value="value => (answers[question.id] = value)"
    />
    <div v-if="!isSubmitted" class="mt-2 flex items-center justify-end gap-3">
      <span
        v-if="submitFailed"
        class="ltr:mr-auto rtl:ml-auto text-xs font-medium text-red-600 dark:text-red-400"
      >
        {{ labels.error }}
      </span>
      <button
        type="button"
        class="rounded-lg px-4 py-2 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
        :style="{ backgroundColor: accent }"
        :disabled="!canSubmit || submitting"
        @click="onSubmit"
      >
        {{ labels.submit }}
      </button>
    </div>
  </div>
</template>
